import { useEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { X } from 'lucide-react';
import { adsApi } from '@/api/client';
import { parseSbBidRecommendations, parseSbKeywordSuggestionTable } from '@/utils/sbAdGroupCreate';
import { SuggestedBidDisplay } from '@/components/campaign-create/create-adgroup/SuggestedBidDisplay';
import {
  applySuggestedBidsToAddedKeywords,
  buildKeywordRecommendationTargets,
  chunkItems,
  expandKeywordsForMatchTypes,
  KEYWORD_RECOMMENDATION_TARGET_CHUNK,
  keywordRowKey,
  mergeAddedKeywords,
  parseKeywordList,
  parseKeywordRecommendationsPayload,
  suggestedBidMapFromRecommendations,
} from '@/utils/spCampaignCreateDraft';
import type { SpAddedKeyword, SpAdvertisedProduct, SpBiddingStrategy, SpMatchType } from '@/types/spCampaignCreate';
import { currencySymbol } from '@/utils/formatCurrency';
import { Spinner } from '@/components/ui/Spinner';

const MATCH_OPTIONS: SpMatchType[] = ['BROAD', 'PHRASE', 'EXACT'];

export function KeywordTargetingPanel({
  profileId,
  advertisedProducts,
  biddingStrategy,
  defaultBid,
  added,
  onChange,
  disabled,
  seedLoading = false,
  allowEmptyAsins = false,
  framed = true,
  suggestedLead,
  addedLead,
  extraAddedCount = 0,
  onRemoveAll,
  onApplyAllSuggested,
  adProduct = 'SP',
  campaignId,
  landingUrl,
}: {
  profileId: string;
  advertisedProducts: SpAdvertisedProduct[];
  biddingStrategy: SpBiddingStrategy;
  defaultBid: string;
  added: SpAddedKeyword[];
  onChange: Dispatch<SetStateAction<SpAddedKeyword[]>>;
  disabled?: boolean;
  /** Parent is still resolving ASINs (e.g. automatic SB collections). */
  seedLoading?: boolean;
  /** Automatic SB collections have no advertised ASINs until catalog seed returns. */
  allowEmptyAsins?: boolean;
  framed?: boolean;
  suggestedLead?: ReactNode;
  addedLead?: ReactNode;
  extraAddedCount?: number;
  onRemoveAll?: () => void;
  onApplyAllSuggested?: () => void;
  adProduct?: 'SP' | 'SB';
  campaignId?: string;
  /** Store page URL. Sponsored Brands keyword ideas use this instead of ASINs. */
  landingUrl?: string;
}) {
  const [tab, setTab] = useState<'suggested' | 'list'>('suggested');
  const [matchTypes, setMatchTypes] = useState<SpMatchType[]>(['BROAD', 'PHRASE', 'EXACT']);
  const [useSuggestedBid, setUseSuggestedBid] = useState(true);
  const [listText, setListText] = useState('');
  const [listStatus, setListStatus] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<SpAddedKeyword[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingBidKeys, setPendingBidKeys] = useState<Set<string>>(() => new Set());

  const asins = useMemo(
    () => advertisedProducts.map((p) => p.asin).filter(Boolean),
    [advertisedProducts]
  );
  const asinKey = asins.join(',');
  const addedKeys = useMemo(
    () => new Set(added.map((row) => keywordRowKey(row.keywordText, row.matchType))),
    [added]
  );

  useEffect(() => {
    const storeUrl = adProduct === 'SB' ? landingUrl?.trim() : '';
    if (!asinKey && !allowEmptyAsins && !storeUrl) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    if (adProduct === 'SB' && !storeUrl && !asinKey) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const request =
      adProduct === 'SB'
        ? adsApi.getSbKeywordRecommendations(profileId, {
            ...(storeUrl ? { url: storeUrl } : { asins: asinKey.split(',') }),
            ...(campaignId ? { campaignId: campaignId.toString() } : {}),
            maxNumSuggestions: 50,
            goal: 'PAGE_VISIT',
            creativeType: 'PRODUCT_COLLECTION',
          })
        : adsApi.getSpKeywordRecommendations(profileId, {
            recommendationType: 'KEYWORDS_FOR_ASINS',
            asins: asinKey ? asinKey.split(',') : [],
            biddingStrategy,
            sortDimension: 'DEFAULT',
            maxRecommendations: 50,
          });
    void request
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.message || 'No related keywords.');
          setSuggestions([]);
          return;
        }
        const parsed =
          adProduct === 'SB'
            ? parseSbKeywordSuggestionTable(res.data)
            : parseKeywordRecommendationsPayload(res.data);
        setSuggestions(
          parsed.map((row) => ({
            keywordText: row.keywordText,
            matchType: row.matchType,
            suggestedBid: row.suggestedBid,
            suggestedBidMin: row.suggestedBidMin ?? null,
            suggestedBidMax: row.suggestedBidMax ?? null,
            bid: row.suggestedBid != null ? String(row.suggestedBid) : defaultBid,
          }))
        );
      })
      .catch(() => {
        if (cancelled) return;
        setError('No related keywords. Try entering your own keywords.');
        setSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adProduct, allowEmptyAsins, asinKey, biddingStrategy, campaignId, defaultBid, landingUrl, profileId]);

  const visibleSuggestions = suggestions.filter((row) => matchTypes.includes(row.matchType));

  const applyBid = (row: SpAddedKeyword): SpAddedKeyword => {
    if (useSuggestedBid && row.suggestedBid != null) {
      return { ...row, bid: String(row.suggestedBid) };
    }
    return { ...row, bid: row.bid || defaultBid };
  };

  const fetchBidsForAddedKeywords = (
    rows: SpAddedKeyword[],
    options?: { forceApplyBid?: boolean }
  ) => {
    if (rows.length === 0 || (adProduct !== 'SB' && asins.length === 0)) return;
    const keys = rows.map((row) => keywordRowKey(row.keywordText, row.matchType));
    const applySuggested = useSuggestedBid;
    const forceApplyBid = options?.forceApplyBid === true;
    const fallbackBid = defaultBid;
    setPendingBidKeys((prev) => {
      const next = new Set(prev);
      for (const key of keys) next.add(key);
      return next;
    });
    const chunks = chunkItems(rows, KEYWORD_RECOMMENDATION_TARGET_CHUNK);
    const requests =
      adProduct === 'SB'
        ? chunks.map((chunk) =>
            adsApi.getSbKeywordBidRecommendations(profileId, {
              ...(campaignId ? { campaignId: campaignId.toString() } : {}),
              adFormat: 'productCollection',
              keywords: chunk.map((row) => ({
                keywordText: row.keywordText,
                matchType: row.matchType.toLowerCase() as 'broad' | 'phrase' | 'exact',
              })),
            })
          )
        : chunkItems(buildKeywordRecommendationTargets(rows), KEYWORD_RECOMMENDATION_TARGET_CHUNK).map(
            (targets) =>
              adsApi.getSpKeywordRecommendations(profileId, {
                recommendationType: 'KEYWORDS_FOR_ASINS',
                asins,
                biddingStrategy,
                sortDimension: 'CLICKS',
                maxRecommendations: 0,
                targets,
              })
          );
    void Promise.all(requests)
      .then((responses) => {
        const suggestedByKey = new Map(suggestedBidMapFromRecommendations([]));
        for (const res of responses) {
          if (!res.success) continue;
          const parsed = suggestedBidMapFromRecommendations(
            adProduct === 'SB'
              ? parseSbBidRecommendations(res.data)
              : parseKeywordRecommendationsPayload(res.data)
          );
          for (const [key, value] of parsed) suggestedByKey.set(key, value);
        }
        if (suggestedByKey.size === 0) return;
        onChange((current) =>
          applySuggestedBidsToAddedKeywords(current, suggestedByKey, {
            useSuggestedBid: applySuggested,
            defaultBid: fallbackBid,
            forceBid: forceApplyBid,
          })
        );
      })
      .catch(() => {
        /* Keep default bids; suggested column stays empty. */
      })
      .finally(() => {
        setPendingBidKeys((prev) => {
          const next = new Set(prev);
          for (const key of keys) next.delete(key);
          return next;
        });
      });
  };

  const addRows = (rows: SpAddedKeyword[]) => {
    const existingKeys = new Set(added.map((row) => keywordRowKey(row.keywordText, row.matchType)));
    const mapped = rows.map(applyBid);
    const newlyAdded = mapped.filter(
      (row) => !existingKeys.has(keywordRowKey(row.keywordText, row.matchType))
    );
    const { next, added: count } = mergeAddedKeywords(added, mapped);
    onChange(next);
    // SB keyword ideas omit bids; fetch them so Sugg. bid / Apply all have values.
    const needsBids = newlyAdded.filter((row) => row.suggestedBid == null);
    if (needsBids.length > 0) fetchBidsForAddedKeywords(needsBids);
    return count;
  };

  const toggleMatch = (matchType: SpMatchType) => {
    setMatchTypes((prev) =>
      prev.includes(matchType) ? prev.filter((m) => m !== matchType) : [...prev, matchType]
    );
  };

  const addFromText = (raw: string) => {
    const texts = parseKeywordList(raw);
    const incoming = expandKeywordsForMatchTypes(texts, matchTypes);
    const count = addRows(incoming);
    setListStatus(`${count}/${texts.length * matchTypes.length} keywords were added successfully.`);
    setListText('');
  };

  const totalAdded = added.length + extraAddedCount;
  const body = (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex gap-2 text-sm">
            {([
              ['suggested', 'Suggested'],
              ['list', 'Enter list'],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`px-3 py-1 rounded-lg border ${
                  tab === id ? 'border-brand text-brand bg-brand/5' : 'border-border'
                }`}
                onClick={() => setTab(id)}
                disabled={disabled}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-2">
              Bid
              <select
                value={useSuggestedBid ? 'suggested' : 'custom'}
                onChange={(e) => setUseSuggestedBid(e.target.value === 'suggested')}
                className="border border-border rounded-lg px-2 py-1"
                disabled={disabled}
              >
                <option value="suggested">Suggested bid</option>
                <option value="custom">Custom bid</option>
              </select>
            </label>
            <span className="text-xs text-foreground-muted">per click (CPC)</span>
            {MATCH_OPTIONS.map((matchType) => (
              <label key={matchType} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={matchTypes.includes(matchType)}
                  onChange={() => toggleMatch(matchType)}
                  disabled={disabled}
                />
                {matchType.charAt(0) + matchType.slice(1).toLowerCase()}
              </label>
            ))}
          </div>
          {tab === 'suggested' ? (
            <div className="space-y-3">
              {suggestedLead}
              {loading || (seedLoading && visibleSuggestions.length === 0) ? (
                <div className="flex items-center gap-2 text-sm text-foreground-muted">
                  <Spinner className="w-4 h-4" />
                  Loading suggestions…
                </div>
              ) : visibleSuggestions.length === 0 ? (
                <p className="text-sm text-foreground-subtle">
                  {error || 'No related keywords. Try entering your own keywords.'}
                </p>
              ) : (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="flex justify-between px-3 py-2 text-xs text-foreground-muted bg-surface-muted">
                    <span>Keyword</span>
                    <button
                      type="button"
                      className="text-brand"
                      disabled={disabled}
                      onClick={() => addRows(visibleSuggestions)}
                    >
                      Add all
                    </button>
                  </div>
                  <ul className="max-h-72 overflow-auto divide-y divide-border">
                    {visibleSuggestions.map((row) => {
                      const key = keywordRowKey(row.keywordText, row.matchType);
                      const already = addedKeys.has(key);
                      return (
                        <li key={key} className="flex items-center gap-2 px-3 py-2 text-sm">
                          <span className="flex-1 truncate">{row.keywordText}</span>
                          <span className="w-16 text-foreground-muted">{row.matchType.toLowerCase()}</span>
                          <SuggestedBidDisplay
                            suggested={row.suggestedBid}
                            min={row.suggestedBidMin}
                            max={row.suggestedBidMax}
                          />
                          <button
                            type="button"
                            className="text-brand disabled:opacity-40"
                            disabled={disabled || already}
                            onClick={() => addRows([row])}
                          >
                            {already ? 'Added' : 'Add'}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                value={listText}
                onChange={(e) => setListText(e.target.value)}
                placeholder="Enter keywords separated by new line."
                className="w-full min-h-[120px] border border-border rounded-lg px-3 py-2 text-sm"
                disabled={disabled}
              />
              {listStatus && <p className="text-xs text-brand">{listStatus}</p>}
              <div className="flex justify-end">
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
                  disabled={disabled || !listText.trim() || matchTypes.length === 0}
                  onClick={() => addFromText(listText)}
                >
                  Add keywords
                </button>
              </div>
            </div>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">{totalAdded} added</p>
            {totalAdded > 0 && (
              <button
                type="button"
                className="text-sm text-brand"
                disabled={disabled}
                onClick={() => {
                  setPendingBidKeys(new Set());
                  if (onRemoveAll) onRemoveAll();
                  else onChange([]);
                }}
              >
                Remove all
              </button>
            )}
          </div>
          {totalAdded === 0 ? (
            <p className="text-sm text-foreground-subtle border border-dashed border-border rounded-lg p-4">
              Added keywords will appear here.
            </p>
          ) : (
            <div className="divide-y divide-border border border-border rounded-lg max-h-96 overflow-auto">
              <div className="grid grid-cols-[1fr_5.5rem_4.5rem_7rem] gap-2 px-2 py-2 text-xs font-medium text-foreground-muted">
                <span>Keyword</span>
                <span>Match type</span>
                <span>
                  Sugg. bid
                  <button
                    type="button"
                    className="mt-0.5 block font-normal text-brand disabled:text-foreground-subtle"
                    disabled={disabled || totalAdded === 0}
                    onClick={() => {
                      onChange((current) =>
                        current.map((row) =>
                          row.suggestedBid != null
                            ? { ...row, bid: Number(row.suggestedBid).toFixed(2) }
                            : row
                        )
                      );
                      onApplyAllSuggested?.();
                      // SB ideas often land without bids; fetch then force-fill Bid.
                      const missing = added.filter((row) => row.suggestedBid == null);
                      if (missing.length > 0) {
                        fetchBidsForAddedKeywords(missing, { forceApplyBid: true });
                      }
                    }}
                  >
                    Apply all
                  </button>
                </span>
                <span>Bid</span>
              </div>
              {addedLead}
              <ul>
                {added.map((row) => {
                  const key = keywordRowKey(row.keywordText, row.matchType);
                  const bidNum = parseFloat(row.bid);
                  const missingBid = Number.isNaN(bidNum) || bidNum <= 0;
                  return (
                    <li key={key} className="p-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm truncate">{row.keywordText}</p>
                          <p className="text-xs text-foreground-muted">{row.matchType.toLowerCase()}</p>
                        </div>
                        <span className="w-20 text-right">
                          {pendingBidKeys.has(key) ? (
                            <Spinner className="w-3 h-3 inline-block" />
                          ) : (
                            <SuggestedBidDisplay
                              suggested={row.suggestedBid}
                              min={row.suggestedBidMin}
                              max={row.suggestedBidMax}
                            />
                          )}
                        </span>
                        <div className="flex items-center gap-1">
                          <span className="text-xs">{currencySymbol() ?? ''}</span>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={row.bid}
                            onChange={(e) => {
                              const nextBid = e.target.value;
                              onChange((current) =>
                                current.map((item) =>
                                  keywordRowKey(item.keywordText, item.matchType) === key
                                    ? { ...item, bid: nextBid }
                                    : item
                                )
                              );
                            }}
                            className="w-20 border border-border rounded px-2 py-1 text-sm"
                            disabled={disabled}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            onChange((current) =>
                              current.filter((item) => keywordRowKey(item.keywordText, item.matchType) !== key)
                            )
                          }
                          disabled={disabled}
                          aria-label="Remove keyword"
                        >
                          <X className="w-4 h-4 text-foreground-muted" />
                        </button>
                      </div>
                      {missingBid && <p className="text-xs text-error">Enter a bid</p>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
  );

  if (!framed) return body;
  return (
    <fieldset className="border border-border rounded-xl p-4 space-y-3">
      <legend className="text-sm font-medium text-foreground-muted px-1">Keyword targeting</legend>
      {body}
    </fieldset>
  );
}
